import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppContext } from '../App';
import TimeSelect from '../components/TimeSelect';
import BulkCardUploadReview from '../components/BulkCardUploadReview';
import { findUnresolvedConflicts, findCoverageGaps } from '../utils/scheduleConflict';

const API = 'https://api.cpms-srlkevcharge.com';

const DAYS = [
  { key: 'Mon', label: 'Mon' }, { key: 'Tue', label: 'Tue' }, { key: 'Wed', label: 'Wed' },
  { key: 'Thu', label: 'Thu' }, { key: 'Fri', label: 'Fri' }, { key: 'Sat', label: 'Sat' }, { key: 'Sun', label: 'Sun' },
];

const IconPlus = () => (
  <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
const IconTrash = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);
const IconDownload = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);
const IconUpload = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

function DayPicker({ value, onChange }) {
  const selectedDays = value ? value.split(',').map(d => d.trim()) : [];
  const toggleDay = (dayKey) => {
    const isSelected = selectedDays.includes(dayKey);
    const updated = isSelected ? selectedDays.filter(d => d !== dayKey) : [...selectedDays, dayKey];
    onChange(updated.length > 0 ? updated.join(',') : '');
  };
  return (
    <div className="day-picker">
      {DAYS.map(day => (
        <button type="button" key={day.key} className={`day-pill ${selectedDays.includes(day.key) ? 'selected' : ''}`} onClick={() => toggleDay(day.key)} title={day.key}>
          {day.label}
        </button>
      ))}
    </div>
  );
}

function LocationMultiSelect({ selectedIds, allLocations, onChange }) {
  const toggleLocation = (locId) => {
    const isSelected = selectedIds.includes(locId);
    const updated = isSelected ? selectedIds.filter(id => id !== locId) : [...selectedIds, locId];
    onChange(updated);
  };
  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>
        {selectedIds.length === 0 ? 'Applies at every station in the organisation' : `Restricted to ${selectedIds.length} station${selectedIds.length !== 1 ? 's' : ''}`}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {allLocations.map(loc => (
          <button
            type="button" key={loc.id} onClick={() => toggleLocation(loc.id)}
            className={`day-pill ${selectedIds.includes(loc.id) ? 'selected' : ''}`}
            style={{ width: 'auto', borderRadius: 20, padding: '0 12px' }}
          >
            {loc.station_name}
          </button>
        ))}
      </div>
    </div>
  );
}

function TariffScheduleForm({ schedule, tariffs, allLocations, onUpdate, isInConflict }) {
  const [showDays, setShowDays] = useState(!!schedule.days_of_week);
  const [showDates, setShowDates] = useState(!!(schedule.effective_start_date || schedule.effective_end_date));
  const [showStations, setShowStations] = useState((schedule.location_ids || []).length > 0);
  const selectedTariff = tariffs.find(t => String(t.id) === String(schedule.tariff_id));

  return (
    <div className={`entity-card ${isInConflict ? 'has-conflict' : ''}`}>
      <div className="entity-card-header">
        <span className="entity-card-title">{selectedTariff ? selectedTariff.name : 'New Schedule'}</span>
        <button type="button" className="entity-remove-btn" onClick={() => onUpdate('__remove__')}>
          <IconTrash />
        </button>
      </div>

      <div className="form-grid" style={{ gridTemplateColumns: '2fr 1fr 1fr' }}>
        <div className="form-group">
          <label>Select Tariff</label>
          <select value={schedule.tariff_id} onChange={(e) => onUpdate('tariff_id', e.target.value)}>
            <option value="">Select...</option>
            {tariffs.map(t => <option key={t.id} value={t.id}>{t.name} — {t.organisation_name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Start Time</label>
          <TimeSelect value={schedule.start_time} onChange={(val) => onUpdate('start_time', val)} />
        </div>
        <div className="form-group">
          <label>End Time</label>
          <TimeSelect value={schedule.end_time} onChange={(val) => onUpdate('end_time', val)} />
        </div>
      </div>

      <div className="advanced-toggle-row" style={{ flexWrap: 'wrap' }}>
        <label className="advanced-toggle">
          <input type="checkbox" checked={showDays} onChange={() => setShowDays(p => !p)} /> Specific days
        </label>
        <label className="advanced-toggle">
          <input type="checkbox" checked={showDates} onChange={() => setShowDates(p => !p)} /> Specific date range
        </label>
        <label className="advanced-toggle">
          <input type="checkbox" checked={showStations} onChange={() => setShowStations(p => !p)} /> Specific stations
        </label>
      </div>

      {showDays && (
        <div className="advanced-section">
          <div className="advanced-section-label">Applies on these days (leave unselected for every day)</div>
          <DayPicker value={schedule.days_of_week} onChange={(val) => onUpdate('days_of_week', val)} />
        </div>
      )}

      {showDates && (
        <div className="advanced-section">
          <div className="advanced-section-label">Effective date range (leave end date blank for ongoing)</div>
          <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <div className="form-group">
              <label>Start Date</label>
              <input type="date" value={schedule.effective_start_date || ''} onChange={(e) => onUpdate('effective_start_date', e.target.value)} />
            </div>
            <div className="form-group">
              <label>End Date</label>
              <input type="date" value={schedule.effective_end_date || ''} onChange={(e) => onUpdate('effective_end_date', e.target.value)} />
            </div>
          </div>
        </div>
      )}

      {showStations && (
        <div className="advanced-section">
          <div className="advanced-section-label">Restrict this tariff to specific stations (leave none selected for organisation-wide)</div>
          <LocationMultiSelect selectedIds={schedule.location_ids || []} allLocations={allLocations} onChange={(ids) => onUpdate('location_ids', ids)} />
        </div>
      )}
    </div>
  );
}

export default function AddRFIDCard() {
  const navigate = useNavigate();
  const { showToast } = useAppContext();

  const [organisations, setOrganisations] = useState([]);
  const [tariffs, setTariffs] = useState([]);
  const [tariffLookup, setTariffLookup] = useState({});
  const [allLocations, setAllLocations] = useState([]);

  const [form, setForm] = useState({ uid: '', organisation_id: '', free_charging: 'No', rfid_type: 'Prepaid', description: '' });
  const [schedules, setSchedules] = useState([]);
  const [errors, setErrors] = useState({});
  const [unresolvedConflicts, setUnresolvedConflicts] = useState([]);
  const [gapWarnings, setGapWarnings] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Bulk upload state ──────────────────────────────────────────────────
  const [uploadStage, setUploadStage] = useState('none');
  const [bulkData, setBulkData] = useState(null);
  const [registering, setRegistering] = useState(false);
  const fileInputRef = React.useRef(null);

  useEffect(() => {
    axios.get(`${API}/organisations/`).then(res => setOrganisations(res.data)).catch(() => {});
    axios.get(`${API}/tariffs/`).then(res => {
      setTariffs(res.data);
      const lookup = {};
      res.data.forEach(t => { lookup[t.id] = t.name; });
      setTariffLookup(lookup);
    }).catch(() => {});
    axios.get(`${API}/locations/`).then(res => setAllLocations(res.data)).catch(() => {});
  }, []);

  const validate = () => {
    const e = {};
    if (!form.uid.trim()) e.uid = 'Card UID is required';
    if (form.uid.includes(' ')) e.uid = 'Card UID cannot contain spaces';
    return e;
  };

  const handleChange = (name, value) => {
    setForm(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
  };

  const addSchedule = () => {
    setSchedules(prev => [
      ...prev,
      { tempId: Date.now(), tariff_id: '', start_time: '', end_time: '', days_of_week: '', effective_start_date: '', effective_end_date: '', priority: 0, location_ids: [] }
    ]);
    setSubmitAttempted(false);
  };

  const updateSchedule = (tempId, field, value) => {
    if (field === '__remove__') {
      setSchedules(prev => prev.filter(s => s.tempId !== tempId));
    } else {
      setSchedules(prev => prev.map(s => s.tempId === tempId ? { ...s, [field]: value } : s));
    }
    setSubmitAttempted(false);
  };

  const resolveConflict = (tempIdWinner) => {
    setSchedules(prev => {
      const updated = prev.map(s => s.tempId === tempIdWinner ? { ...s, priority: (s.priority || 0) + 1 } : s);
      const withTariff = updated.filter(s => s.tariff_id);
      if (withTariff.length >= 2) {
        const conflicts = findUnresolvedConflicts(withTariff, tariffLookup);
        setUnresolvedConflicts(conflicts.map(c => ({ ...c, tempIdA: withTariff[c.indexA].tempId, tempIdB: withTariff[c.indexB].tempId })));
      } else {
        setUnresolvedConflicts([]);
      }
      return updated;
    });
  };

  const isInConflict = (tempId) => unresolvedConflicts.some(c => c.tempIdA === tempId || c.tempIdB === tempId);

  const runValidation = () => {
    const withTariff = schedules.filter(s => s.tariff_id);
    let conflicts = [];
    if (withTariff.length >= 2) {
      conflicts = findUnresolvedConflicts(withTariff, tariffLookup);
      setUnresolvedConflicts(conflicts.map(c => ({ ...c, tempIdA: withTariff[c.indexA].tempId, tempIdB: withTariff[c.indexB].tempId })));
    } else {
      setUnresolvedConflicts([]);
    }
    const gaps = withTariff.length > 0 ? findCoverageGaps(withTariff) : [];
    setGapWarnings(gaps);
    return { conflicts, gaps };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    if (form.free_charging !== 'Yes') {
      const { conflicts, gaps } = runValidation();
      if (conflicts.length > 0) return;
      if (gaps.length > 0 && !submitAttempted) {
        setSubmitAttempted(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/cards/`, {
        uid: form.uid,
        organisation_id: form.organisation_id ? Number(form.organisation_id) : null,
        free_charging: form.free_charging,
        rfid_type: form.rfid_type,
        description: form.description || null,
        tariff_schedules: form.free_charging === 'Yes' ? [] : schedules
          .filter(s => s.tariff_id)
          .map(s => ({
            tariff_id: Number(s.tariff_id),
            start_time: s.start_time || null,
            end_time: s.end_time || null,
            days_of_week: s.days_of_week || null,
            effective_start_date: s.effective_start_date || null,
            effective_end_date: s.effective_end_date || null,
            priority: s.priority || 0,
            location_ids: s.location_ids || [],
          })),
      });
      showToast(`Card ${form.uid} registered successfully`);
      navigate('/rfid-cards');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to register card', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Bulk upload handlers ─────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    window.open(`${API}/bulk-cards/template`, '_blank');
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const fd = new FormData();
    fd.append('file', file);

    setUploadStage('reading');
    try {
      const res = await axios.post(`${API}/bulk-cards/preview`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkData(res.data);
      setUploadStage('reviewed');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to read the uploaded file', 'error');
      setUploadStage('none');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpdateBulkCard = (index, updatedCard) => {
    setBulkData(prev => ({
      ...prev,
      cards: prev.cards.map((c, i) => i === index ? updatedCard : c)
    }));
  };

  const handleRemoveBulkCard = (index) => {
    setBulkData(prev => ({
      ...prev,
      cards: prev.cards.filter((_, i) => i !== index),
    }));
  };

  const isBulkCardValid = (c) => (c.errors || []).length === 0 && c.uid;

  const validBulkCount = bulkData ? bulkData.cards.filter(isBulkCardValid).length : 0;

  const handleBulkRegister = async () => {
    const validCards = bulkData.cards.filter(isBulkCardValid);
    if (validCards.length === 0) {
      showToast('No fully valid cards to register', 'error');
      return;
    }

    const payload = {
      cards: validCards.map(c => ({
        uid: c.uid,
        organisation_name: c.organisation_name || null,
        rfid_type: c.rfid_type || 'Prepaid',
        status: c.status || 'Active',
        free_charging: c.free_charging || 'No',
        description: c.description || null,
        tariff_schedules: c.free_charging === 'Yes' ? [] : (c.tariff_matches || [])
          .filter(m => m.found)
          .map(m => ({
            tariff_id: m.tariff_id,
            start_time: m.start_time || null,
            end_time: m.end_time || null,
            days_of_week: m.days_of_week || null,
            effective_start_date: m.effective_start_date || null,
            effective_end_date: m.effective_end_date || null,
            priority: m.priority || 0,
            location_ids: [],
          }))
      }))
    };

    setRegistering(true);
    try {
      const res = await axios.post(`${API}/bulk-cards/register`, payload);
      showToast(res.data.message);
      navigate('/rfid-cards');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Bulk registration failed', 'error');
    } finally {
      setRegistering(false);
    }
  };

  const cancelBulkUpload = () => {
    setUploadStage('none');
    setBulkData(null);
  };

  return (
    <>
      <button className="back-link" onClick={() => navigate('/rfid-cards')}>← Back to RFID Cards</button>

      <div className="page-header">
        <h1>Add RFID Card</h1>
        <p>Register a new RFID card and optionally assign it tariffs</p>
      </div>

      {uploadStage === 'none' && (
        <div className="evse-import-banner">
          <div className="evse-import-banner-text">
            <h3>Register cards using a spreadsheet</h3>
            <p>Download the template, fill it in, and upload it. Everything is validated before anything is registered.</p>
          </div>
          <div className="evse-import-actions">
            <button className="btn btn-outline-green" onClick={handleDownloadTemplate}>
              <IconDownload /> Download Template
            </button>
            <button className="btn btn-green" onClick={() => fileInputRef.current?.click()}>
              <IconUpload /> Upload Completed File
            </button>
          </div>
        </div>
      )}

      {uploadStage === 'reading' && (
        <div className="card empty-state">
          <h3>Reading your file...</h3>
          <p>Validating cards and resolving tariffs.</p>
        </div>
      )}

      {uploadStage === 'reviewed' && bulkData && (
        <>
          <div className="preview-summary-bar">
            <div className="preview-summary-text">
              <span className="good">{validBulkCount} card{validBulkCount !== 1 ? 's' : ''} ready to register</span>
              {bulkData.cards.length - validBulkCount > 0 && (
                <> · <span className="bad">{bulkData.cards.length - validBulkCount} need attention</span></>
              )}
              {' '}from {bulkData.total_rows} row{bulkData.total_rows !== 1 ? 's' : ''}
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={cancelBulkUpload}>Cancel</button>
              <button
                className="btn btn-green"
                onClick={handleBulkRegister}
                disabled={registering || validBulkCount === 0}
              >
                {registering ? 'Registering...' : `Register ${validBulkCount} Card${validBulkCount !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>

          <BulkCardUploadReview
            cards={bulkData.cards}
            onUpdateCard={handleUpdateBulkCard}
            onRemoveCard={handleRemoveBulkCard}
          />
        </>
      )}

      {uploadStage === 'none' && (
      <div className="card" style={{ padding: 24, maxWidth: 760 }}>
        <form onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group full">
              <label>Card UID <span className="required">*</span></label>
              <input
                type="text" placeholder="e.g. 04A3B2C1"
                value={form.uid}
                onChange={(e) => handleChange('uid', e.target.value)}
                style={errors.uid ? { borderColor: 'var(--red)' } : {}}
              />
              {errors.uid
                ? <span className="hint" style={{ color: 'var(--red)' }}>{errors.uid}</span>
                : <span className="hint">The unique identifier printed on or encoded in the RFID card</span>}
            </div>

            <div className="form-group full">
              <label>Organisation</label>
              <select value={form.organisation_id} onChange={(e) => handleChange('organisation_id', e.target.value)}>
                <option value="">None</option>
                {organisations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
              </select>
            </div>

            <div className="form-group full">
              <label>RFID Type</label>
              <select value={form.rfid_type} onChange={(e) => handleChange('rfid_type', e.target.value)}>
                <option value="Prepaid">Prepaid</option>
                <option value="Postpaid">Postpaid</option>
              </select>
            </div>

            <div className="form-group full">
              <label>Free Charging</label>
              <select value={form.free_charging} onChange={(e) => handleChange('free_charging', e.target.value)}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
              <span className="hint">If Yes, this card is never billed and no tariffs need to be assigned.</span>
            </div>

            <div className="form-group full">
              <label>Description</label>
              <textarea
                rows={2} placeholder="Optional notes about this card..."
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                style={{ resize: 'vertical', fontFamily: 'inherit', fontSize: 14, padding: '10px 13px', border: '1.5px solid var(--border)', borderRadius: 7 }}
              />
            </div>
          </div>

          {form.free_charging !== 'Yes' && (
            <div style={{ marginTop: 20 }}>
              <label style={{ display: 'block', marginBottom: 10 }}>Assigned Tariffs</label>

              {unresolvedConflicts.map((conflict, i) => (
                <div className="collision-choice-box" key={i}>
                  <div className="collision-choice-message">{conflict.message}</div>
                  <div className="collision-choice-buttons">
                    <button type="button" className="collision-choice-btn" onClick={() => resolveConflict(conflict.tempIdA)}>
                      Give priority to {tariffLookup[schedules.find(s => s.tempId === conflict.tempIdA)?.tariff_id]}
                    </button>
                    <button type="button" className="collision-choice-btn" onClick={() => resolveConflict(conflict.tempIdB)}>
                      Give priority to {tariffLookup[schedules.find(s => s.tempId === conflict.tempIdB)?.tariff_id]}
                    </button>
                  </div>
                </div>
              ))}

              {gapWarnings.length > 0 && unresolvedConflicts.length === 0 && (
                <div className="schedule-gap-banner">
                  <div className="schedule-gap-banner-title">⚠ Some periods have no card-level tariff — station's own tariff will apply then</div>
                  <ul className="schedule-gap-banner-list">
                    {gapWarnings.map((g, i) => <li key={i}>{g}</li>)}
                  </ul>
                </div>
              )}

              {schedules.length === 0 && (
                <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
                  No tariffs assigned. This card will simply use whatever tariff each station charges.
                </div>
              )}

              {schedules.map(schedule => (
                <TariffScheduleForm
                  key={schedule.tempId}
                  schedule={schedule}
                  tariffs={tariffs}
                  allLocations={allLocations}
                  onUpdate={(field, value) => updateSchedule(schedule.tempId, field, value)}
                  isInConflict={isInConflict(schedule.tempId)}
                />
              ))}

              <button type="button" className="add-entity-btn" onClick={addSchedule}>
                <IconPlus /> Add Tariff
              </button>
            </div>
          )}

          <div style={{ marginTop: 20, display: 'flex', gap: 10 }}>
            <button type="submit" className="btn btn-green" disabled={submitting}>
              {submitting ? 'Registering...' : (gapWarnings.length > 0 && submitAttempted ? 'Register Anyway' : 'Register Card')}
            </button>
            <button type="button" className="btn btn-ghost" onClick={() => navigate('/rfid-cards')}>Cancel</button>
          </div>
        </form>
      </div>
      )}

      <input
        type="file" accept=".xlsx,.xls" ref={fileInputRef}
        style={{ display: 'none' }} onChange={handleFileSelected}
      />
    </>
  );
}